import classNames from "classnames";
import React, { FC, useEffect, useRef, useState } from "react";
import { LuChevronDown, LuX } from "react-icons/lu";

import { SelectOptionType } from "../../../../types/types";
import { BillingCheckbox, ClientAvatar } from "../../ui/BillingUi";

export const MAX_INVOICE_CLIENTS = 8;

type Props = {
  selected: SelectOptionType[];
  options: SelectOptionType[];
  search: string;
  onSearch: (value: string) => void;
  onChange: (selected: SelectOptionType[]) => void;
  onLoadMore?: () => void;
  isLoading?: boolean;
  isLoadingMore?: boolean;
  max?: number;
};

const toClient = (option: SelectOptionType) => ({
  id: String(option.value),
  name: String(option.label || "").replace(/ \(synced\)$/, ""),
  isOrganization: option.avatarColor === "purple",
});

/** Multi-select client dropdown from the V49 design (up to 8 clients). */
export const InvoiceClientPicker: FC<Props> = ({
  selected,
  options,
  search,
  onSearch,
  onChange,
  onLoadMore,
  isLoading,
  isLoadingMore,
  max = MAX_INVOICE_CLIENTS,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isAtMax = selected.length >= max;
  const selectedIds = new Set(selected.map((item) => item.value));

  useEffect(() => {
    if (!isOpen) return;
    const close = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [isOpen]);

  const toggle = (option: SelectOptionType) => {
    if (selectedIds.has(option.value)) {
      onChange(selected.filter((item) => item.value !== option.value));
    } else if (!isAtMax) {
      onChange([...selected, option]);
    }
  };

  return (
    <div ref={ref} className={"relative max-w-[480px]"}>
      <div
        role={"button"}
        tabIndex={0}
        aria-label={"Client"}
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setIsOpen((open) => !open);
          }
        }}
        className={classNames(
          "relative flex min-h-[40px] cursor-pointer flex-wrap items-center gap-1.5 rounded-[9px] border bg-white py-[7px] pl-[11px] pr-[30px]",
          isOpen ? "border-[#6B4EFF]" : "border-[#DFE2E8]",
        )}>
        {selected.map((option) => {
          const client = toClient(option);
          return (
            <span
              key={client.id}
              className={
                "inline-flex flex-none items-center gap-[7px] whitespace-nowrap rounded-[7px] bg-[#EDE9FE] py-1 pl-[5px] pr-[7px] text-[12.5px] font-semibold text-[#5B43D6]"
              }>
              <ClientAvatar client={client} size={20} />
              {client.name}
              <button
                type={"button"}
                aria-label={`Remove ${client.name}`}
                onClick={(event) => {
                  event.stopPropagation();
                  toggle(option);
                }}
                className={"inline-flex text-[#A99DE8] hover:text-[#5B43D6]"}>
                <LuX size={12} />
              </button>
            </span>
          );
        })}
        {!selected.length && (
          <span className={"text-[13px] text-[#9AA0B0]"}>Search...</span>
        )}
        <LuChevronDown
          size={14}
          className={
            "absolute right-[11px] top-1/2 -translate-y-1/2 text-[#9097A8]"
          }
        />
      </div>

      {isOpen && (
        <div
          className={
            "absolute left-0 right-0 top-[calc(100%+6px)] z-[21] flex flex-col rounded-[10px] border border-[#E8E8EC] bg-white p-2 shadow-[0_14px_36px_rgba(20,22,34,0.18)]"
          }>
          <input
            autoFocus
            value={search}
            onChange={(event) => onSearch(event.target.value)}
            placeholder={"Search clients..."}
            className={
              "mb-1.5 h-[34px] w-full rounded-lg border border-[#E4E6EB] px-3 text-[13px] text-[#1F2433] outline-none placeholder:text-[#9AA0B0] focus:border-[#6B4EFF]"
            }
          />
          <div
            className={"max-h-[230px] overflow-y-auto"}
            onScroll={(event) => {
              const el = event.currentTarget;
              if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
                onLoadMore?.();
              }
            }}>
            {options.map((option) => {
              const client = toClient(option);
              const isSelected = selectedIds.has(option.value);
              const isDisabled = !isSelected && isAtMax;
              return (
                <div
                  key={client.id}
                  role={"option"}
                  aria-selected={isSelected}
                  aria-disabled={isDisabled}
                  onClick={() => toggle(option)}
                  className={classNames(
                    "flex items-center gap-2.5 rounded-lg px-2.5 py-2",
                    isDisabled
                      ? "cursor-not-allowed opacity-50"
                      : "cursor-pointer hover:bg-[#F4F2FF]",
                  )}>
                  <BillingCheckbox
                    checked={isSelected}
                    disabled={isDisabled}
                    label={client.name}
                    size={18}
                    onChange={() => toggle(option)}
                  />
                  <ClientAvatar client={client} />
                  <span className={"text-[13.5px] font-medium text-[#1F2433]"}>
                    {option.label}
                  </span>
                </div>
              );
            })}
            {(isLoading || isLoadingMore) && (
              <div className={"px-2.5 py-2 text-[13px] text-[#9097A8]"}>
                Loading...
              </div>
            )}
            {!isLoading && !options.length && (
              <div className={"px-2.5 py-2 text-[13px] text-[#9097A8]"}>
                No clients found
              </div>
            )}
          </div>
          <div
            className={
              "mt-[5px] flex items-center justify-between gap-2.5 border-t border-[#F0F1F4] px-1 pb-0.5 pt-2"
            }>
            <button
              type={"button"}
              onClick={() => onChange([])}
              className={"text-[12.5px] font-bold text-[#6B7185]"}>
              Clear all
            </button>
            <span
              className={classNames(
                "text-xs font-bold",
                isAtMax ? "text-[#C0392B]" : "text-[#9097A8]",
              )}>
              {selected.length}/{max} selected
            </span>
            <button
              type={"button"}
              onClick={() => setIsOpen(false)}
              className={
                "rounded-[7px] bg-[#6B4EFF] px-4 py-1.5 text-[12.5px] font-bold text-white hover:bg-[#5A3EEB]"
              }>
              Done
            </button>
          </div>
          {isAtMax && (
            <div
              role={"status"}
              className={
                "mt-1.5 rounded-[7px] bg-[#FEECEC] px-2.5 py-1.5 text-center text-[11.5px] font-semibold text-[#C0392B]"
              }>
              Maximum of {max} clients reached
            </div>
          )}
        </div>
      )}
    </div>
  );
};
